import React, { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
    Fade,
    Divider,
    TextField
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { parfums } from '../data/parfums';

const WHATSAPP_PHONE = '221710338061';

const ParfumDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [quantity, setQuantity] = useState(1);

    const parfum = parfums.find(p => p.id === id);

    const handleOrderViaWhatsApp = () => {
        if (!parfum) return;

        const message = `🎁 Bonjour ! Je souhaite commander:\n\n✨ ${parfum.name}\n📦 Quantité: ${quantity}\n💰 Prix unitaire: ${parfum.price?.toLocaleString('fr-TN')} TND\n💵 Total: ${(parfum.price! * quantity).toLocaleString('fr-TN')} TND\n\nPouvez-vous me confirmer la disponibilité et les modalités de livraison ? Merci !`;
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`, '_blank');
    };

    if (!parfum) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                    Parfum non trouvé
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Le parfum que vous recherchez n'existe pas.
                </Typography>
                <Link to="/parfums" style={{ color: theme.palette.secondary.main }}>
                    Retour aux parfums
                </Link>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Fade in timeout={500}>
                <Box>
                    <Box sx={{ mb: 4 }}>
                        <Link
                            to="/parfums"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.secondary.main,
                                textDecoration: 'none',
                                marginBottom: 16
                            }}
                        >
                            <ArrowBackIcon sx={{ mr: 1 }} />
                            Retour aux parfums
                        </Link>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
                        <Box sx={{ flex: 1 }}>
                            <Fade in timeout={700}>
                                <Box>
                                    <img
                                        src={parfum.image}
                                        alt={parfum.name}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: 8,
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                        }}
                                    />
                                </Box>
                            </Fade>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Fade in timeout={900}>
                                <Box>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        {parfum.isNew && (
                                            <Chip
                                                label="Nouveau"
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme.palette.secondary.main,
                                                    color: 'black',
                                                    fontWeight: 600
                                                }}
                                            />
                                        )}
                                        {parfum.isLuxury && (
                                            <Chip
                                                label="Luxe"
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'black',
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600
                                                }}
                                            />
                                        )}
                                        <Chip
                                            label={parfum.category === 'homme' ? 'Homme' : parfum.category === 'femme' ? 'Femme' : 'Mixte'}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                borderColor: theme.palette.secondary.main,
                                                color: theme.palette.secondary.main
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant={isMobile ? 'h3' : 'h2'}
                                        sx={{
                                            mb: 2,
                                            color: theme.palette.text.primary,
                                            fontWeight: 600
                                        }}
                                    >
                                        {parfum.name}
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                        sx={{ mb: 3, fontStyle: 'italic' }}
                                    >
                                        {parfum.shortDescription}
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        sx={{ mb: 4, lineHeight: 1.7 }}
                                    >
                                        {parfum.description}
                                    </Typography>

                                    {parfum.price && (
                                        <Box sx={{ mb: 4 }}>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {parfum.price.toLocaleString('fr-TN')} TND
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <TextField
                                            type="number"
                                            label="Quantité"
                                            value={quantity}
                                            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                            inputProps={{ min: 1, max: 100 }}
                                            sx={{ width: 120 }}
                                        />
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<WhatsAppIcon />}
                                            onClick={handleOrderViaWhatsApp}
                                            fullWidth
                                            sx={{
                                                backgroundColor: '#25D366',
                                                color: 'white',
                                                px: 4,
                                                py: 2,
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                borderRadius: '50px',
                                                textTransform: 'none',
                                                flex: 1,
                                                '&:hover': {
                                                    backgroundColor: '#128C7E',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Commander via WhatsApp
                                        </Button>
                                    </Box>

                                    <Divider sx={{ my: 4 }} />

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mb: 3,
                                            color: theme.palette.text.primary,
                                            fontWeight: 600
                                        }}
                                    >
                                        Notes Olfactives
                                    </Typography>

                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                        gap: 3
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600,
                                                    mb: 2
                                                }}
                                            >
                                                Tête
                                            </Typography>
                                            {parfum.notes.tete.map((note, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {note}
                                                </Typography>
                                            ))}
                                        </Box>

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600,
                                                    mb: 2
                                                }}
                                            >
                                                Cœur
                                            </Typography>
                                            {parfum.notes.coeur.map((note, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {note}
                                                </Typography>
                                            ))}
                                        </Box>

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600,
                                                    mb: 2
                                                }}
                                            >
                                                Fond
                                            </Typography>
                                            {parfum.notes.fond.map((note, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {note}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Fade>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Container>
    );
};

export default ParfumDetailPage;
